import { Injectable, inject } from '@angular/core';
import { getApp } from 'firebase/app';
import {
  MessagePayload,
  Messaging,
  getMessaging,
  isSupported,
  onMessage,
  onRegistered,
  register,
} from 'firebase/messaging';
import { Subject, lastValueFrom } from 'rxjs';

import { NotificationsService, environment } from '@bookly/shared';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationsService {
  readonly #notificationsService = inject(NotificationsService);
  readonly #messageSubject = new Subject<unknown>();
  #messagingPromise: Promise<Messaging | null> | null = null;
  public message$ = this.#messageSubject.asObservable();

  async #registerAndGetFid(
    messaging: Messaging,
    serviceWorkerRegistration: ServiceWorkerRegistration
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      let unsubscribe: () => void = () => {};
      unsubscribe = onRegistered(messaging, fid => {
        unsubscribe();
        resolve(fid);
      });

      void register(messaging, {
        serviceWorkerRegistration,
        vapidKey: environment.vapidKey,
      }).catch(error => {
        unsubscribe();
        reject(error);
      });
    });
  }

  async #getMessaging(): Promise<Messaging | null> {
    if (!this.#messagingPromise) {
      this.#messagingPromise = isSupported()
        .then(supported => {
          return supported ? getMessaging(getApp()) : null;
        })
        .catch(() => null);
    }

    return this.#messagingPromise;
  }

  public async init() {
    const messaging = await this.#getMessaging();

    if (!messaging || !('serviceWorker' in navigator)) {
      return;
    }

    const serviceWorkerRegistration = await navigator.serviceWorker.ready;
    const permission = await Notification.requestPermission();
    // Compatibility note: `deviceToken` now stores the FID-based registration id
    // returned by `onRegistered`, not the legacy token from deprecated `getToken`.
    const storedDeviceToken = localStorage.getItem('deviceToken');
    let deviceRegistrationId: string | null = null;

    if (permission === 'granted') {
      deviceRegistrationId = await this.#registerAndGetFid(
        messaging,
        serviceWorkerRegistration
      );

      if (deviceRegistrationId && deviceRegistrationId !== storedDeviceToken) {
        try {
          await lastValueFrom(
            this.#notificationsService.registerDeviceToken(deviceRegistrationId)
          );
          localStorage.setItem('deviceToken', deviceRegistrationId);
        } catch (error) {
          console.error(error);
        }
      }
    }

    onMessage(messaging, (message: MessagePayload) => {
      this.#messageSubject.next(message.notification);
    });
  }
}
